import Catalog from 'files/Catalog.js';

/*
 * NetworkCoverage
 *
 * Turns a description of a home into the devices that home needs.
 *
 * The shape of this comes from the in-home presentation, which ends with the
 * advisor saying: "if you'll take me for a short walk through your home, I'll
 * show you where they go. We like to start in the master bedroom." That is the
 * whole model — a walkthrough, room by room, in the order a person would
 * actually walk it. Not a funnel, not a wizard about budget.
 *
 * Every placement rule below is taken from the owner's manuals, which cite
 * NFPA 72. Nothing here is a sales heuristic dressed up as a code requirement,
 * and each rule carries the reason it exists so the page can say why rather
 * than just assert:
 *
 *   Smoke (OSS20)  required in every sleeping room; outside each sleeping area
 *                  within 21 ft of any bedroom door; on every level including
 *                  basements. NOT in kitchens, baths, garages, attics,
 *                  crawlspaces, laundry or furnace rooms.
 *
 *   CO (OSCO20)    required on every level including the basement, and within
 *                  21 ft of any bedroom door. NOT in kitchens, garages,
 *                  attics, crawlspaces, or within 20 ft of a combustion source
 *                  — which is why the furnace room gets heat, not CO.
 *
 *   Heat (OSH20)   for the rooms a smoke sensor cannot watch: kitchens (120°F),
 *                  garages (135°F), attics (174°F), laundry (120°F), utility
 *                  and furnace rooms (135°F), basements and crawlspaces
 *                  (135°F). NOT in bedrooms or hallways — those need smoke.
 *
 * A "requirement" of 'required' means a manual or NFPA 72 says the device is
 * required, not that we are requiring it. Everything else is 'recommended'.
 */

// Devices the network cannot work without, whatever the home looks like.
const ALWAYS = [{
    handle: 'comm-link',
    quantity: 1,
    reason: 'Nothing reaches your phone without the hub — it is what turns a set of alarms into a network.',
    requirement: 'required'
}];

const bedroomStep = {
    devices: [{
        handle: 'smoke-sensor',
        per: 1,
        reason: 'NFPA 72 requires a smoke alarm inside every sleeping room.',
        requirement: 'required'
    },{
        handle: 'bed-shaker',
        per: 1,
        reason: 'Most fatal home fires happen at night. A horn alone does not reliably wake children, adults over 40, or anyone who has been drinking or taking a sleep aid.',
        requirement: 'recommended'
    }],
    help: 'We start where the presentation starts — the bedrooms. Every sleeping room needs its own smoke sensor; that is the code minimum, not our recommendation.',
    icon: 'moon-stars-fill',
    id: 'bedrooms',
    kind: 'count',
    options: [1, 2, 3, 4, 5, 6],
    question: 'How many bedrooms are in your home?',
    title: 'Bedrooms'
};

const levelStep = {
    devices: [{
        handle: 'smoke-sensor',
        per: 1,
        reason: 'A smoke alarm is required outside each sleeping area, within 21 ft of any bedroom door, on every level.',
        requirement: 'required'
    },{
        handle: 'co-sensor',
        per: 1,
        reason: 'A CO sensor is required on every level of the home, within 21 ft of any bedroom door.',
        requirement: 'required'
    }],
    help: 'Count every floor you can sleep above or below — including a finished basement. Each one needs its own smoke and CO sensor in the hallway.',
    icon: 'layers-fill',
    id: 'levels',
    kind: 'count',
    options: [1, 2, 3],
    question: 'How many levels does your home have?',
    title: 'Levels & hallways'
};

const livingStep = {
    devices: [{
        handle: 'smoke-sensor',
        per: 1,
        reason: 'The living area is where a fire is most likely to start and grow before anyone sees it.',
        requirement: 'recommended'
    }],
    help: 'Living rooms, family rooms, dens — the rooms where people actually spend the evening.',
    icon: 'tv-fill',
    id: 'living',
    kind: 'count',
    options: [0, 1, 2, 3],
    question: 'How many living areas are there?',
    title: 'Living areas'
};

// Rooms that get a heat sensor rather than a smoke sensor, because the normal
// activity in them would set a smoke alarm off. Temperature settings are the
// manual's recommendation per room.
const spaceStep = {
    help: 'These are the rooms a smoke alarm cannot watch — the steam, dust, and exhaust in them cause false alarms. A heat sensor covers them instead, set to the temperature that room should never reach.',
    icon: 'house-door-fill',
    id: 'spaces',
    kind: 'multi',
    options: [{
        devices: [{ handle: 'heat-sensor', per: 1, reason: 'Many house fires start in the kitchen, but a smoke alarm within 5 ft of the cooking appliances would go off every time you sear something. Heat sensor, set to 120°F.', requirement: 'recommended' }],
        icon: 'egg-fried',
        id: 'kitchen',
        label: 'Kitchen'
    },{
        devices: [{ handle: 'heat-sensor', per: 1, reason: 'Exhaust gases will not trigger a heat sensor the way they trigger a smoke alarm. Set to 135°F.', requirement: 'recommended' }],
        icon: 'building',
        id: 'garage',
        label: 'Garage'
    },{
        devices: [{ handle: 'heat-sensor', per: 1, reason: 'Attics run hot and dusty, so the setting goes to 174°F — high enough not to nuisance-alarm in August.', requirement: 'recommended' }],
        icon: 'house-exclamation-fill',
        id: 'attic',
        label: 'Attic'
    },{
        devices: [{ handle: 'heat-sensor', per: 1, reason: 'Dryer lint is highly flammable and a leading cause of laundry fires. Low-level gases will not trigger the sensor. Set to 120°F.', requirement: 'recommended' }],
        icon: 'basket2',
        id: 'laundry',
        label: 'Laundry room'
    },{
        devices: [{ handle: 'heat-sensor', per: 1, reason: 'A furnace room needs heat rather than CO — a CO sensor has to sit at least 20 ft from any combustion source. Set to 135°F.', requirement: 'recommended' }],
        icon: 'fire',
        id: 'utility',
        label: 'Utility / furnace room'
    },{
        devices: [
            { handle: 'smoke-sensor', per: 1, reason: 'A smoke alarm is required in the basement, near the bottom of the stairs to the floor above.', requirement: 'required' },
            { handle: 'heat-sensor', per: 1, reason: 'Basements are dusty enough that a heat sensor covers the mechanical side without false alarms. Set to 135°F.', requirement: 'recommended' }
        ],
        icon: 'stack',
        id: 'basement',
        label: 'Basement'
    },{
        devices: [{ handle: 'heat-sensor', per: 1, reason: 'Crawlspaces are unconditioned, so a smoke alarm is not rated for them. Set to 135°F.', requirement: 'recommended' }],
        icon: 'door-closed-fill',
        id: 'crawlspace',
        label: 'Crawlspace'
    }],
    question: 'Which of these does your home have?',
    title: 'The rooms smoke alarms miss'
};

// Water. Kept as its own step because it is the whole catalogue for a
// water-only dealership and an add-on for everyone else.
const waterStep = {
    help: 'A water sensor goes anywhere water runs or sits. Tap to add — bump the count if you have more than one.',
    icon: 'droplet-fill',
    id: 'water',
    kind: 'quantity',
    options: [
        { icon: 'thermometer-high', id: 'water-heater', label: 'Water heater' },
        { icon: 'basket2', id: 'washer', label: 'Washing machine' },
        { icon: 'wind', id: 'hvac', label: 'HVAC / furnace' },
        { icon: 'egg-fried', id: 'dishwasher', label: 'Dishwasher' },
        { icon: 'snow2', id: 'fridge', label: 'Fridge with ice or water' },
        { icon: 'water', id: 'sump', label: 'Sump pump' },
        { icon: 'droplet', id: 'kitchen-sink', label: 'Kitchen sink' },
        { icon: 'moisture', id: 'utility-sink', label: 'Utility sink' },
        { icon: 'droplet', id: 'bath-sink', label: 'Bathroom sink' },
        { icon: 'badge-wc-fill', id: 'toilet', label: 'Toilet' }
    ],
    question: 'Where does water run in your home?',
    title: 'Water'
};

const NetworkCoverage = {

    // A water-only dealership walks the water step alone; everyone else walks
    // the whole home.
    steps: mode => {
        return mode === 'water'
            ? [waterStep]
            : [bedroomStep, levelStep, livingStep, spaceStep, waterStep];
    },

    /*
     * answers shape:
     *   { bedrooms: 3, levels: 2, living: 1,
     *     spaces: ['kitchen','garage'],
     *     water: { 'water-heater': 1, toilet: 3 } }
     *
     * Returns one row per device with a merged quantity and the reasons that
     * produced it, so the result can explain itself rather than just present
     * a number.
     */
    plan: (answers = {}, mode = 'full') => {

        let rows = {};

        const add = ({ handle, quantity, reason, requirement }) => {
            if(quantity <= 0) {
                return;
            }
            if(!rows[handle]) {
                rows[handle] = { handle: handle, quantity: 0, reasons: [], requirement: 'recommended' };
            }
            rows[handle].quantity += quantity;
            // 'required' wins — one required placement makes the line required
            if(requirement === 'required') {
                rows[handle].requirement = 'required';
            }
            if(reason && rows[handle].reasons.includes(reason) === false) {
                rows[handle].reasons.push(reason);
            }
        };

        NetworkCoverage.steps(mode).forEach(step => {

            if(step.kind === 'count') {
                let count = parseInt(answers[step.id]) || 0;
                step.devices.forEach(device => add({
                    ...device,
                    quantity: device.per * count
                }));
                return;
            }

            if(step.kind === 'multi') {
                let selected = answers[step.id] || [];
                step.options
                    .filter(option => selected.includes(option.id))
                    .forEach(option => option.devices.forEach(device => add({
                        ...device,
                        quantity: device.per
                    })));
                return;
            }

            if(step.kind === 'quantity') {
                let quantities = answers[step.id] || {};
                let total = Object.values(quantities).reduce((sum, value) => sum + (parseInt(value) || 0), 0);
                add({
                    handle: 'water-sensor',
                    quantity: total,
                    reason: 'One sensor per place water can escape — under the sink, behind the fridge, beside the heater.',
                    requirement: 'recommended'
                });
                return;
            }
        });

        // The hub only earns its place once there is something to connect.
        let hasDevices = Object.keys(rows).length > 0;
        if(hasDevices) {
            ALWAYS.forEach(add);
        }

        // Results follow the catalogue's own order rather than the order the
        // walkthrough happened to ask its questions in, so the list reads the
        // same way every time and matches the products page. The catalogue is
        // the single place that order is decided.
        let order = Catalog.getAll(mode).map(product => product.handle);
        let devices = Object.values(rows).sort((a, b) => {
            return order.indexOf(a.handle) - order.indexOf(b.handle);
        });

        return {
            devices: devices,
            total: devices.reduce((sum, row) => sum + row.quantity, 0)
        };
    }
}

export default NetworkCoverage;
